import React, { useEffect } from 'react';
import type { Theme } from '@material-ui/core';
import { Grid, Paper } from '@material-ui/core';
import { Block, Typography } from '@klp/design';
import { NumberFormatted } from '@klp/formgenerator/components/NumberFormatted';
import { useTranslation } from 'react-i18next';

import { useKlpStyles } from '@klp/design/Theme/useKlpStyles';
import { KlpFontVariant } from '@klp/design/Theme/KlpFontVariant';
import { useNokkeltallSammendrag } from '../context/useNokkeltallSammendrag';
import { useKostnadsKalkyleContext } from '../context/KostnadsKalkyleProvider';

const styles = (theme: Theme) => ({
    paper: {
        height: '100%',
        '& > div': {
            height: '100%',
        },
    },
    heading: {
        fontWeight: 600,
        fontSize: KlpFontVariant.epsilon.fontSize,
        marginBottom: theme.spacing(1),
    },
    itemText: {
        display: 'flex',
        justifyContent: 'space-between',
    },
    valueText: {
        fontWeight: 'bold',
        color: theme.palette.primary.main,
    },
});

export const NokkeltallSammendrag: React.FC = () => {
    const { t } = useTranslation();
    const classes = useKlpStyles({}, styles);
    const { totalKostnadFormValue, setTotalKostnadFormValue } =
        useKostnadsKalkyleContext();

    const { nokkeltall } = useNokkeltallSammendrag();

    // Pass the totalKostnad value to the module context
    // and update the form value when it changes
    useEffect(() => {
        if (
            nokkeltall?.totalKostnad === null ||
            nokkeltall?.totalKostnad === undefined ||
            totalKostnadFormValue === nokkeltall.totalKostnad
        ) {
            return;
        }

        setTotalKostnadFormValue(nokkeltall.totalKostnad);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nokkeltall?.totalKostnad]);

    const currency = ' kr';

    if (!nokkeltall) return null;

    return (
        <Grid container={true} spacing={2}>
            <Grid item={true} xs={12} md={4}>
                <Paper className={classes.paper}>
                    <Block padding="tiny">
                        <Block padding="tiny">
                            <Typography
                                className={classes.heading}
                                translation="prosjekt.kostnadskalkyle.summary.section_project"
                            />
                            <Typography className={classes.itemText}>
                                {t(
                                    'prosjekt.kostnadskalkyle.summary.totalKostnad',
                                )}
                                :
                                <NumberFormatted
                                    className={classes.valueText}
                                    value={nokkeltall.totalKostnad}
                                    suffix={currency}
                                />
                            </Typography>
                            <Typography className={classes.itemText}>
                                {t(
                                    'prosjekt.kostnadskalkyle.summary.kvadratMeterPris',
                                )}
                                :
                                <NumberFormatted
                                    className={classes.valueText}
                                    value={nokkeltall.kvadratMeterPris}
                                    suffix={currency}
                                />
                            </Typography>
                        </Block>
                    </Block>
                </Paper>
            </Grid>

            <Grid item={true} xs={12} md={4}>
                <Paper className={classes.paper}>
                    <Block padding="tiny">
                        <Block padding="tiny">
                            <Typography
                                className={classes.heading}
                                translation="prosjekt.kostnadskalkyle.summary.section_financial"
                            />
                            <Typography className={classes.itemText}>
                                {t(
                                    'prosjekt.kostnadskalkyle.summary.andelAktiveres',
                                )}
                                :
                                <NumberFormatted
                                    className={classes.valueText}
                                    value={nokkeltall.andelAktiveres}
                                    suffix={currency}
                                />
                            </Typography>
                            <Typography className={classes.itemText}>
                                {t(
                                    'prosjekt.kostnadskalkyle.summary.andelKostnadsfores',
                                )}
                                :
                                <NumberFormatted
                                    className={classes.valueText}
                                    value={nokkeltall.andelKostnadsfores}
                                    suffix={currency}
                                />
                            </Typography>
                        </Block>
                    </Block>
                </Paper>
            </Grid>

            <Grid item={true} xs={12} md={4}>
                <Paper className={classes.paper}>
                    <Block padding="tiny">
                        <Block padding="tiny">
                            <Typography
                                className={classes.heading}
                                translation="prosjekt.kostnadskalkyle.summary.section_historical"
                            />
                            <Typography className={classes.itemText}>
                                {t(
                                    'prosjekt.kostnadskalkyle.summary.gjennomsnittligKostnadPerM2',
                                )}
                                :
                                <NumberFormatted
                                    className={classes.valueText}
                                    value={
                                        nokkeltall.gjennomsnittligKostnadPerM2
                                    }
                                    suffix={currency}
                                />
                            </Typography>
                        </Block>
                    </Block>
                </Paper>
            </Grid>
        </Grid>
    );
};
